import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styles from './Engine.module.css'
import { useUser } from '../../contexts/userContext'
import { SelectOption } from '../../common/components/Select/Select'
import { EngineRunState, getRunState, LearningGoal } from './EngineHelper'
import { LoginStep } from './LoginStep'
import {
  DEFAULT_MAP_OPTIONS,
  ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION,
  ENGINE_LANGUAGE_SELECT_OPTIONS,
} from '../../common/constants'
import { SingleValue } from 'react-select'
import i18n from '../../i18n'
import { useNotification } from '../../contexts/NotificationContext'
import { useConfirmation } from '../../contexts/ConfirmationContext'
import { Task } from '../../types/commonTypes'
import { getLearningGoals, getNextJsonSnippet, getTaskFromJson } from './EngineGoalsAndTaskParser'
import { GameForm } from '../GameEditor/types'
import { EngineLanguage, MapType } from '../../api/gameTypes'
import { useGame } from '../../contexts/GameContext'
import { sleep } from '../../util/functional'
import { Upload } from '@/lovable/pages/Upload'
import { LenseSelection } from '@/lovable/pages/LenseSelection'
import { Tasks } from '@/lovable/pages/Tasks'
import { Board } from '@/lovable/pages/Board'
import { Publish } from '@/lovable/pages/Publish'
import { useCredits } from '@/lovable/contexts/CreditContext'

enum EngineStep {
  LOGIN = 'LOGIN',
  SILENT_LOGIN = 'SILENT_LOGIN',
  FILL_FORM = 'FILL_FORM',
  LENSE = 'LENSE',
  INTERACTIVE = 'INTERACTIVE',
  BOARD = 'BOARD',
  PUBLISH = 'PUBLISH',
  FINAL = 'FINAL',
}

export type EngineForm = {
  gameName: string
  instructorToAdd: string
  generatorPin: string
  webSources: string
  freeText: string
  inputFiles: File[]
  language: 'English' | 'Finnish' | 'Portuguese' | 'Dutch' | 'Norwegian' | 'Swedish' | 'Deutch' | 'Spanish'
  template: string
  targetGroup: string
  readyTasks: boolean
  lense: string
}

export const Engine: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [mainStep, setMainStep] = useState(EngineStep.SILENT_LOGIN)
  const [initialValues] = useState<Partial<EngineForm>>({
    gameName: 'Your game',
    instructorToAdd: user?.name,
    generatorPin: '',
    webSources: '',
    inputFiles: undefined,
    language: 'English',
    template: 'random',
    targetGroup: '',
    readyTasks: false,
  })
  const [formData, setFormData] = useState(initialValues)
  const [formDataToSend, setFormDataToSend] = useState<FormData>()
  const intervalPoller = useRef(0)
  const [engineRunId, setEngineRunId] = useState(0)
  const [runState, setRunState] = useState<Partial<EngineRunState> | null>({
    fileUploaded: false,
    matchPairsCreated: false,
    multiChoiceCreated: false,
    checkBoxCreated: false,
    missingWordCreated: false,
    creativeCreated: false,
  })
  const [errorStatus, setErrorStatus] = useState<string | null>(null)
  const { notifyError } = useNotification()
  const errorShown = useRef(false)
  const { requestConfirmation } = useConfirmation()
  const [has3D, setHas3D] = useState(false)
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language)
  // We probably need this, but it's not shown in UI currently
  //const [roundsLeft, setRoundsLeft] = useState(0)
  const [interactiveRound, setInteractiveRound] = useState(1)
  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [learningGoals, setLearningGoals] = useState<LearningGoal[]>([])
  const [taskSlots, setTaskSlots] = useState<{ [key: number]: Task }>({})
  const { createGame, createOrUpdateTask, gameData } = useGame()
  const [selectedGameBoardUrl, setSelectedGameBoardUrl] = useState<string>()
  const [gameCreated, setGameCreated] = useState(false)
  const tasksCreated = useRef(false)
  const [shouldPoll, setShouldPoll] = useState(true)
  const [autoPrompt, setAutoPrompt] = useState<string | null>(null)
  const [waitingGameBoard, setWaitingGameBoard] = useState(false)
  const [readyTasks, setReadyTasks] = useState(false)
  const [is3D, setIs3D] = useState(false)
  const resetDoneForRound = useRef(-1)
  const [imageCreditsUsed, setImageCreditsUsed] = useState(0)
  const { updateCredits, currentGameCredits, setInitialCredits } = useCredits()
  const [latestSubContent, setLatestSubContent] = useState<string>()
  const [learningGoalsReady, setLearningGoalsReady] = useState(false)
  const [okToParseLearningGoals, setOkToParseLearningGoals] = useState(true)

  const handleLearningGoalsReady = useCallback(() => {
    setLearningGoalsReady(true)
  }, [])

  useEffect(() => {
    if (gameData?.gameId) {
      if (!tasksCreated.current) {
        tasksCreated.current = true
        Object.values(taskSlots)
          .slice(1)
          .forEach(async (task, index) => {
            await sleep(index * 100)
            createOrUpdateTask({ ...task, order: (task.order ?? 1) - 1 })
          })
        setTimeout(() => {
          setGameCreated(true)
        }, Object.values(taskSlots).length * 200 + 1000)
      }
    }
  }, [createOrUpdateTask, gameData, taskSlots])

  useEffect(() => {
    console.log('shouldPoll ', shouldPoll)
    const POLL_INTERVAL = 750
    const MAX_TIME = 80000 // 60 secods
    let roundsLeft = MAX_TIME / POLL_INTERVAL
    if (engineRunId > 0 && (shouldPoll || waitingGameBoard)) {
      setErrorStatus(null)
      intervalPoller.current = window.setInterval(async function () {
        if (roundsLeft-- < 0) {
          //Cancel this due to timeout
          setErrorStatus(t('engine.polling_timeout_message', 'That request was a bit tricky for me. Please try again'))
          window.clearInterval(intervalPoller.current)
        }
        const getIt = await fetch(`${process.env.REACT_APP_ENGINE_BACKEND}/engine_file?run_id=${engineRunId}`)
        const content = await getIt.text()
        const rState = getRunState(content, gameCreated, setShouldPoll)
        if (rState.error) {
          setErrorStatus(
            t('engine.polling_general_error_message', 'That request was too tricky for me. Please try again'),
          )
          window.clearInterval(intervalPoller.current)
        }
        if (rState.latestSubContent) setLatestSubContent(rState.latestSubContent)
        if (rState.previewRounds && ![EngineStep.INTERACTIVE, EngineStep.FINAL, EngineStep.BOARD].includes(mainStep)) {
          setMainStep(EngineStep.INTERACTIVE)
        }
        if (selectedGameBoardUrl) {
          setTimeout(() => {
            setMainStep(EngineStep.FINAL)
          }, 1000)
        }
        if (rState.allReady || gameCreated) {
          window.clearInterval(intervalPoller.current)
        }
        setRunState(rState)
      }, POLL_INTERVAL)
    } else {
      if (intervalPoller.current) {
        window.clearInterval(intervalPoller.current)
      }
    }

    return () => {
      window.clearInterval(intervalPoller.current)
    }
  }, [engineRunId, mainStep, t, gameCreated, selectedGameBoardUrl, shouldPoll, waitingGameBoard])

  useEffect(() => {
    if (runState?.usage && imageCreditsUsed < 1) {
      updateCredits(Math.floor(runState.usage?.total / 1000))
    }
  }, [runState?.usage, updateCredits, imageCreditsUsed])

  useEffect(() => {
    try {
      const needToAutoPrompt: string[] = []
      if (runState?.previewRounds && runState?.previewRounds[0]) {
        const allReceivedAnswers = runState.previewRounds[0]
        if (
          allReceivedAnswers.indexOf('TASK_DELETED') > -1 &&
          allReceivedAnswers.indexOf('TASK_DELETED') < 10 &&
          resetDoneForRound.current < interactiveRound
        ) {
          const taskId = parseInt(
            allReceivedAnswers.substring(allReceivedAnswers.indexOf('TASK_DELETED') + 'TASK_DELETED'.length + 1),
          )
          resetDoneForRound.current = interactiveRound
          setTaskSlots((prev) => {
            const temp = prev
            delete temp[taskId]
            return temp
          })
        }
        if (
          (allReceivedAnswers.includes('ERROR') || allReceivedAnswers.includes('Error:')) &&
          resetDoneForRound.current < interactiveRound
        ) {
          resetDoneForRound.current = interactiveRound
          setErrorStatus(
            t('engine.polling_general_error_message', 'That request was too tricky for me. Please try again'),
          )
          return
        }

        let startIndex = 0
        let maxRounds = 100
        if (!learningGoalsReady && shouldPoll && okToParseLearningGoals) {
          try {
            const lgs = getLearningGoals(startIndex, allReceivedAnswers)
            if (lgs.jsonObject) {
              setLearningGoals(lgs.jsonObject)
              startIndex = -1
            }
          } catch (e) {
            console.log(e)
          }
        }

        while (startIndex > -1 && maxRounds-- > 0) {
          const next = getNextJsonSnippet(startIndex, allReceivedAnswers)
          if (next.jsonObject) {
            try {
              const task = getTaskFromJson(1, next.jsonObject, selectedTemplate.includes('3D'))
              if (typeof task === 'number' || typeof task === 'string' || task === null || task === undefined) {
                console.log('malformed task found: ' + task)
                console.log(next.jsonObject)
                if (task) {
                  needToAutoPrompt.push(task)
                } else {
                  needToAutoPrompt.push(next.jsonObject.slotId)
                }
              } else {
                setTaskSlots((prev) => {
                  if (prev) {
                    return {
                      ...prev,
                      [task.order ?? 0]: task,
                    }
                  } else {
                    return {}
                  }
                })
              }
            } catch (e) {
              console.log('parsing failed for ', next.jsonObject.slotId)
              console.log(e)
              needToAutoPrompt.push(next.jsonObject.slotId)
            }
          } else {
            try {
              console.log('-malformad task found')
              const text = allReceivedAnswers.substring(startIndex)
              const taskIdPart = text.split('"slotId"')[1]
              console.log('id: ' + parseInt(taskIdPart))
              if (!isNaN(parseInt(taskIdPart))) {
                needToAutoPrompt.push(parseInt(taskIdPart).toString())
              }
            } catch (e) {
              console.log('Parsing had failed and failed to push the id')
              console.log(e)
            }
          }
          startIndex = next.nextStartIndex
        }
      }
      if (needToAutoPrompt && needToAutoPrompt.length > 0 && !shouldPoll) {
        console.log(runState?.previewRounds)
        console.log('\n-----\nSETTING AUTO PROMPT NOW')
        setAutoPrompt(needToAutoPrompt.join(' ,'))
      }
    } catch (e) {
      console.log('Failed to parse tasks')
      console.log(e)
    }
  }, [
    interactiveRound,
    learningGoalsReady,
    runState?.previewRounds,
    selectedTemplate,
    shouldPoll,
    t,
    okToParseLearningGoals,
  ])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [mainStep])

  const handleStartAgain = useCallback(() => {
    if (intervalPoller.current) window.clearInterval(intervalPoller.current)
    setTimeout(() => {
      errorShown.current = false
    }, 2000)
    setEngineRunId(0)
    setErrorStatus(null)
    setMainStep(EngineStep.SILENT_LOGIN)
    window.location.reload()
  }, [])

  useEffect(() => {
    if (errorShown.current) return
    if (errorStatus?.includes('Invalid') || errorStatus?.includes('not found')) {
      errorShown.current = true
      handleStartAgain()
      notifyError({
        customDismissLabel: t('engine.error.try_again', 'Try again'),
        title: t('engine.error.pin_or_account.title', 'Pin or account not found'),
        content: (
          <div className={styles.notificationContent}>
            {t(
              'engine.error.pin_or_account.content',
              'We weren’t able to verify your PIN or account. Please double-check and try again or reach out if you need support!',
            )}
          </div>
        ),
      })
    } else if (errorStatus?.includes('No rounds left')) {
      errorShown.current = true
      handleStartAgain()
      notifyError({
        title: t('engine.error.no_rounds.title', 'Out of rounds!'),
        content: (
          <div className={styles.notificationContent}>
            {t(
              'engine.error.no_rounds.content',
              'Your attempts have been used up. Reach out to our support and get more!',
            )}
          </div>
        ),
      })
    } /*else if (errorStatus) {
      errorShown.current = true
      handleStartAgain()
      notifyError({
        customDismissLabel: t('engine.error.try_again', 'Try again'),
        title: t('engine.error.generic.title', 'Uh-oh! It seems I need a moment'),
        content: (
          <div className={styles.notificationContent}>
            {t(
              'engine.error.generic.content',
              'An unexpected error occurred, but don’t worry — no engine rounds were lost! Please wait 5 minutes and try again.',
            )}
          </div>
        ),
      })
    }*/
  }, [errorStatus, handleStartAgain, notifyError, t])

  const handleSubmit = useCallback(
    async (values: Partial<EngineForm>) => {
      if (mainStep === EngineStep.LOGIN || mainStep === EngineStep.SILENT_LOGIN) {
        const pinResponse = await fetch(
          `https://seppo-proxy.herokuapp.com/${process.env.REACT_APP_OLD_UI_BASE_URL}/engine_pin_check_only.json?email=${values.instructorToAdd}&pin_code=${values.generatorPin}`,
        )
        const reply = await pinResponse.text()
        if (!reply.includes('OK')) {
          setErrorStatus(reply)
          setMainStep(EngineStep.LOGIN)
          return
        }
        if (reply.includes('HAS_3D')) setHas3D(true)
        setFormData({ ...formData, ...values })
        try {
          const roundsLeft = JSON.parse(reply).rounds_left
          setInitialCredits(roundsLeft)
        } catch (e) {}
        setMainStep(EngineStep.FILL_FORM)
      } else if (mainStep === EngineStep.FILL_FORM) {
        setFormData({ ...formData, ...values })
        setReadyTasks(values.readyTasks ? true : false)
        setIs3D(values.template?.includes('3D') ?? false)
        setMainStep(values.readyTasks ? EngineStep.INTERACTIVE : EngineStep.LENSE)

        const tempData = new FormData()
        tempData.append('gameName', 'Game name')
        tempData.append('instructorToAdd', user?.name!)
        tempData.append('generatorPin', user?.enginePin!)
        tempData.append('language', EngineLanguage[currentLanguage as keyof typeof EngineLanguage])
        tempData.append('template', values.template!)
        tempData.append('webSources', values.webSources!)
        tempData.append('freeText', values.freeText!)
        tempData.append('targetGroup', '')
        tempData.append('readyTasks', values.readyTasks ? 'true' : 'false')
        if (values.inputFiles) {
          values.inputFiles.forEach((file, index) => {
            tempData.append('inputFiles', file)
          })
        }
        setFormDataToSend(tempData)
        if (values.readyTasks) {
          setMainStep(EngineStep.INTERACTIVE)
          // Do the actual submitting here
          const xhr = new XMLHttpRequest()
          xhr.open('POST', `${process.env.REACT_APP_ENGINE_BACKEND}/engine_submit`)
          xhr.onload = function (event: any) {
            if (event.target.response.includes('Error occurred')) {
              setErrorStatus(event.target.response)
            } else {
              // const roundsLeft = parseInt(event.target.response.split('rounds left: ')[1])
              // setRoundsLeft(roundsLeft)
              setEngineRunId(event.target.response.split('=')[1])
            }
          }

          if (tempData) {
            tempData.append('lense', '')
            setFormDataToSend(tempData)
            xhr.send(tempData)
          }
        }
      } else if (mainStep === EngineStep.LENSE) {
        setMainStep(EngineStep.INTERACTIVE)
        // Do the actual submitting here
        const xhr = new XMLHttpRequest()
        xhr.open('POST', `${process.env.REACT_APP_ENGINE_BACKEND}/engine_submit`)
        xhr.onload = function (event: any) {
          if (event.target.response.includes('Error occurred')) {
            setErrorStatus(event.target.response)
          } else {
            // const roundsLeft = parseInt(event.target.response.split('rounds left: ')[1])
            // setRoundsLeft(roundsLeft)
            setEngineRunId(event.target.response.split('=')[1])
          }
        }
        const tempData = formDataToSend

        if (tempData) {
          tempData.append('lense', values.lense!)
          setFormDataToSend(tempData)
          xhr.send(tempData)
        }
      }
    },
    [currentLanguage, formData, formDataToSend, mainStep, user?.enginePin, user?.name, setInitialCredits],
  )

  useEffect(() => {
    if (user && user.enginePin && mainStep === EngineStep.SILENT_LOGIN) {
      handleSubmit({ instructorToAdd: user.name, generatorPin: user.enginePin })
    }
  }, [handleSubmit, user, mainStep])

  const handleUserPrompt = useCallback(
    (prompt: string) => {
      setErrorStatus(null)
      if (prompt.includes('GOAL_PROMPT')) {
        setLearningGoals([])
        setOkToParseLearningGoals(false)
        setTimeout(() => {
          setOkToParseLearningGoals(true)
        }, 7000)
      }
      var xhr = new XMLHttpRequest()
      xhr.open(
        'GET',
        `${
          process.env.REACT_APP_ENGINE_BACKEND
        }/engine_prompt?round=${interactiveRound}&run_id=${engineRunId}&prompt=${encodeURIComponent(prompt)}`,
      )
      xhr.send()
      setInteractiveRound((prev) => prev + 1)
    },
    [engineRunId, interactiveRound],
  )

  const handleRequestGameBoard = useCallback(
    (prompt: string, aspectRatio: string) => {
      var xhr = new XMLHttpRequest()
      if (formData.template?.includes('3D')) {
        //For the 3D games there is no image prompting
        xhr.open(
          'GET',
          `${
            process.env.REACT_APP_ENGINE_BACKEND
          }/engine_prompt?round=${interactiveRound}&run_id=${engineRunId}&prompt=${encodeURIComponent('CENCEL_NOW')}`,
        )
      } else {
        xhr.open(
          'GET',
          `${
            process.env.REACT_APP_ENGINE_BACKEND
          }/engine_prompt?round=${interactiveRound}&run_id=${engineRunId}&prompt=${encodeURIComponent(
            'IMAGE_PROMPT||' + aspectRatio + '||' + prompt,
          )}`,
        )
      }
      xhr.send()
      setImageCreditsUsed((prev) => prev + 10)
      updateCredits(Math.floor(currentGameCredits + 10))
      setSelectedGameBoardUrl(undefined)
      setTimeout(() => {
        setWaitingGameBoard(true)
        setShouldPoll(true)
      }, 2000)
      setInteractiveRound((prev) => prev + 1)
    },
    [engineRunId, interactiveRound, formData.template, updateCredits, currentGameCredits],
  )

  useEffect(() => {
    setSelectedGameBoardUrl(
      runState?.gameBoardUrls && runState?.gameBoardUrls.length > 0
        ? runState?.gameBoardUrls[runState?.gameBoardUrls.length - 1]
        : undefined,
    )
  }, [runState?.gameBoardUrls])

  const handleCancelGameBoard = useCallback(() => {
    setWaitingGameBoard(false)
    setShouldPoll(false)
  }, [])

  const handleGameBoardReady = useCallback(() => {
    setWaitingGameBoard(false)
    setShouldPoll(false)
  }, [])

  const handleCreateGame = useCallback(
    async (gameName: string) => {
      const keywords = ['ENGPIN_' + formData.generatorPin, '']
      if (formData.template?.includes('3D')) {
        keywords.push('Seppo 3D Game')
        if (formData.template === 'news3D') {
          keywords.push(`ENGN_3D${process.env.REACT_APP_NEWS_ID}`)
        }
        if (formData.template === 'esg3D') {
          keywords.push(`ENGN_3D${process.env.REACT_APP_ESG_ID}`)
        }
        if (formData.template === 'nature3D') {
          keywords.push(`ENGN_3D${process.env.REACT_APP_NATURE_ID}`)
        }
        if (formData.template === 'shop3D') {
          keywords.push(`ENGN_3D${process.env.REACT_APP_SHOP_ID}`)
        }
      }

      const gameBasics: GameForm = {
        name: gameName || formData.gameName || 'Name',
        storyHtml:
          Object.values(taskSlots)[0]?.subtasks?.[0]?.description ??
          'Oops, something went wrong with generating the story.',
        keywords,
        gameBoardSettings: {
          gameBoardType: MapType.STATIC,
          gameBoards: [{ fileName: 'Board1', name: 'Board 1', url: selectedGameBoardUrl! }],
          mapOptions: { center: [50, 50], zoom: DEFAULT_MAP_OPTIONS.zoom },
        },
        notificationSettings: {},
        advancedSettings: {},
      }

      const success = await createGame(gameBasics, currentGameCredits)
      if (!success) {
        notifyError({
          title: t('game_editor.create_game_error_notification.title', 'Failed to create game'),
          content: t(
            'game_editor.create_game_error_notification.content',
            'An error occurred while creating a new game. Please try again or contact us for support.',
          ),
        })
      }
      await fetch(`${process.env.REACT_APP_ENGINE_BACKEND}/engine_kill?run_id=${engineRunId}`)
    },
    [
      createGame,
      formData.gameName,
      formData.generatorPin,
      formData.template,
      notifyError,
      selectedGameBoardUrl,
      t,
      engineRunId,
      currentGameCredits,
      taskSlots,
    ],
  )

  const validate = useCallback(
    (values: EngineForm): Partial<EngineForm> => {
      if (mainStep === EngineStep.LOGIN) {
        return {
          generatorPin: !values.generatorPin ? t('engine.value_required', 'Required field') : undefined,
          instructorToAdd: !values.instructorToAdd ? t('engine.value_required', 'Required field') : undefined,
        }
      }
      if (mainStep === EngineStep.FILL_FORM) {
        if (values.template) {
          setSelectedTemplate(values.template)
        }
        return {
          gameName: !values.gameName ? t('engine.value_required', 'Required field') : undefined,
          template: !values.template ? t('engine.value_required', 'Required field') : undefined,
        }
      }
      return {}
    },
    [mainStep, t],
  )

  const handleChangeLanguage = (selectOption: SingleValue<SelectOption>) => {
    i18n.changeLanguage(selectOption?.value ?? ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION.value)
    setCurrentLanguage(selectOption?.value ?? ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION.value)
  }

  const cancelGeneration = useCallback(async () => {
    const confirm = await requestConfirmation({
      title: t('engine.confirm_cancel_title', 'Confirm canceling'),
      text: t('engine.confirm_cancel_text', 'Are you sure you want to cancel the game generation?'),
      confirmActionText: t('engine.confirm_cancel_button_text', 'Confirm'),
    })
    if (!confirm) {
      return false
    }
    if (engineRunId > 0) {
      await fetch(`${process.env.REACT_APP_ENGINE_BACKEND}/engine_kill?run_id=${engineRunId}`)
    }
    handleStartAgain()
  }, [engineRunId, handleStartAgain, requestConfirmation, t])

  const handleAddLearningGoal = useCallback((goal: LearningGoal) => {
    setLearningGoals((prev) => prev.concat(goal))
  }, [])

  const handleUpdateLearningGoal = useCallback((goal: LearningGoal) => {
    setLearningGoals((prev) => prev.filter((g) => g.id !== goal.id).concat(goal))
  }, [])

  const handleRemoveLearningGoal = useCallback((id: number) => {
    setLearningGoals((prev) => prev.filter((g) => g.id !== id))
  }, [])

  const handle3DContinue = useCallback(() => {
    if (formData.template === 'esg3D') {
      setSelectedGameBoardUrl(
        'https://smartfeet-assets-conlib.s3.amazonaws.com/games/cover_pictures/000/001/966/original/ESGCity_PreviewClean.gif?1728911780',
      )
    } else if (formData.template === 'news3D') {
      setSelectedGameBoardUrl(
        'https://smartfeet-assets-conlib.s3.amazonaws.com/games/cover_pictures/000/001/968/original/NewsRoom_Preview.gif?1730469050',
      )
    } else if (formData.template === 'nature3D') {
      setSelectedGameBoardUrl(
        'https://smartfeet-assets-staging.s3.amazonaws.com/games/cover_pictures/000/008/468/original/Nature_Preview.gif?1739181063',
      )
    } else if (formData.template === 'shop3D') {
      setSelectedGameBoardUrl(
        'https://smartfeet-assets-conlib.s3.amazonaws.com/games/cover_pictures/000/002/033/original/Store_Preview.gif?1739789136',
      )
    }
    setMainStep(EngineStep.PUBLISH)
  }, [formData.template])

  const language =
    ENGINE_LANGUAGE_SELECT_OPTIONS.find((option) => option.value === i18n.language) ??
    ENGINE_LANGUAGE_SELECT_DEFAULT_OPTION

  return (
    <div className={styles.wrapper}>
      {/*<div className={styles.headerRow}>
        <div className={styles.statusText}>{statusText}</div>
        <div className={styles.language}>
          {t('onboarding_wizard.language', 'Language')}
          {': '}
          <Select
            options={ENGINE_LANGUAGE_SELECT_OPTIONS}
            value={language}
            onChange={handleChangeLanguage}
            className={styles.languageSelect}
          />
        </div>
      </div>*/}
      <div
        className={styles.container}
        style={mainStep === EngineStep.INTERACTIVE ? { width: '100%', maxWidth: '100%' } : {}}
      >
        <div className={styles.mainAreaContainer}>
          {mainStep === EngineStep.LOGIN && (
            <LoginStep user={user} onSubmit={handleSubmit} validate={validate} initialValues={formData} />
          )}
          {[EngineStep.FILL_FORM, EngineStep.SILENT_LOGIN].includes(mainStep) && (
            <Upload
              onSubmit={handleSubmit}
              has3D={has3D}
              currentLanguage={language}
              onChangeLanguage={handleChangeLanguage}
            />
          )}
          {mainStep === EngineStep.LENSE && <LenseSelection onSubmit={handleSubmit} />}
          {mainStep === EngineStep.INTERACTIVE && (
            <Tasks
              onUserPromt={handleUserPrompt}
              learningGoals={learningGoals}
              onAddLearningGoal={handleAddLearningGoal}
              onRemoveLearningGoal={handleRemoveLearningGoal}
              onUpdateLearningGoal={handleUpdateLearningGoal}
              taskSlots={taskSlots}
              setShouldPoll={setShouldPoll}
              autoPrompt={autoPrompt}
              setAutoPrompt={setAutoPrompt}
              isPolling={shouldPoll}
              readyTasks={readyTasks}
              is3D={is3D}
              realAnswers={runState?.previewRounds}
              onBackToLenses={() => setMainStep(EngineStep.LENSE)}
              errorStatus={errorStatus}
              onContinue={() => {
                formData.template?.includes('3D') ? handle3DContinue() : setMainStep(EngineStep.BOARD)
              }}
              onCancel={cancelGeneration}
              latestSubContent={latestSubContent}
              setLatestSubContent={setLatestSubContent}
              onDoneWithGoals={handleLearningGoalsReady}
            />
          )}
          {mainStep === EngineStep.BOARD && (
            <Board
              onRequestGameBoard={handleRequestGameBoard}
              currentGameBoardUrl={selectedGameBoardUrl}
              firstTaskPrompt={
                Object.values(taskSlots)[0]?.subtasks?.[0]?.description
                  ? Object.values(taskSlots)[0]?.subtasks[0].description.replace('<p>', '').replace('</p>', '')
                  : ''
              }
              onGameBoardReady={handleGameBoardReady}
              onSelectBoard={(boardUrl: string) => {
                setSelectedGameBoardUrl(boardUrl)
                setMainStep(EngineStep.PUBLISH)
              }}
              onCancelGeneration={handleCancelGameBoard}
              errorStatus={errorStatus}
            />
          )}
          {mainStep === EngineStep.PUBLISH && (
            <Publish
              boardImage={selectedGameBoardUrl}
              onPublish={handleCreateGame}
              onGoBack={() => setMainStep(EngineStep.BOARD)}
              taskCount={Object.values(taskSlots).length}
              goalCount={learningGoals.length}
              gameId={gameData?.gameId}
              gameCreated={gameCreated}
            />
          )}
          {/*mainStep === EngineStep.FINAL && (
            <GeneratedStep
              runState={runState}
              gameCreated={gameCreated}
              gameName={formData.gameName ?? ''}
              gameId={gameId}
              gameMapUrl={selectedGameBoardUrl ?? ''}
              language={currentLanguage}
            />
          )*/}
        </div>
        {/*stepIndex === 2 && (
          <div className={styles.stepDisplay}>
            <Button variant='outline-tiny' onClick={cancelGeneration}>
              {t('engine.cancel', 'Cancel generation')}
            </Button>
          </div>
        )*/}
      </div>
    </div>
  )
}
